import { Component, Event, EventEmitter, Host, Listen, Method, Prop, State, h } from '@stencil/core';
import { default as languageStrings } from '../../global/l10n/SearchMytiLists.json';
import * as ListManagerInterfaces from '../add-to-myti-list/add-to-myti-list.interface';
import { fetchWithTimeout } from '@ticom/web-component-utils';
import { Item } from '@ticom/header-components/dist';
import { HTMLTiButtonElement, HTMLTiInputElement, HTMLTiFormElementElement } from '@ticom/ui-components/dist';

@Component({
    tag: 'ti-search-myti-lists',
    styleUrl: './search-myti-lists.scss',
    shadow: true
})
export class SearchMytiLists {

    /**
     * Add tags button reference to disable it if tagLimit reached
     */
    private _addTagsBtnRef: HTMLTiButtonElement;

    /**
     * App settings value to have settings based on user
     */
    private _appSettings: ListManagerInterfaces.AppSettings = null;

    /**
     * Reference to the list form element
     */
    private _listFormField: HTMLTiFormElementElement = null;

    /**
     * Locale-specific set of language strings
     */
    private _localeStrings: Record<string, string>;

    /**
     * Track if tags were modified
     */
    private _modifiedTags: boolean = false;

    /*
     * Input element reference to add tags for lists
     */
    private _tagInputElementRef: HTMLTiInputElement;

    /**
     * Store tag-limit (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _tagLimit: number = null;

    /**
     * Set of tag names to populate ti-chip element
     * NOTE: Set to avoid duplicacy of tags
     */
    private _tags: Set<string> = new Set();

    /**
     * Div ref to show all tags added for list
     */
    private _tagsDivRef: HTMLDivElement;

    /**
     * Store user info data (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Store user lists (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _userLists: ListManagerInterfaces.ListInfo[] = null;

    /**
     * Specifies progress state while data is loaded
     */
    @State() progressState: 'loading' | 'error' | 'ready' = 'loading';

    /**
     * Specifies the list selected in combolist
     */
    @State() selectedList: ListManagerInterfaces.ListInfo = null;

    /**
     * Passed from parent component to uniquely identify events from this instance
     */
    @Prop() contextId: string = null;

    /**
     * Base url for api calls
     */
    @Prop() domain: string;

    /**
     * Header label text
     */
    @Prop() headerLabelText: string = null;

    /**
     * List limit for new list
     */
    @Prop() listLimit: number = null;

    /**
     * Action button for specifically beside list name
     */
    @Prop() listNameActionBtn: boolean = false;

    /**
     * Button text for list name action btn
     */
    @Prop() listNameActionBtnText: string = null;

    /**
     * Specifies the locale
     */
    @Prop() locale: string = 'en-US';

    /**
     * Specifies if modifying tags is allowed
     * Gets overridden by showTags if its false
     */
    @Prop() modifyTags: boolean = true;

    /**
     * Specifies if tags are displayed
     */
    @Prop() showTags: boolean = true;

    /**
     * List id of list to skip from search
     */
    @Prop() skipList: number = null;

    /**
     * Tag limit for new list
     */
    @Prop() tagLimit: number = null;

    /**
     * User data used to have restrictions based on AppSettings
     */
    @Prop() userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * User List Info object
     */
    @Prop() userLists: ListManagerInterfaces.ListInfo[] = null;

    /**
     * Event emitted when list name action button clicked
     */
    @Event() myTiListNameActionBtnEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerBasicEventDetail>;

    /**
     * Event to be emitted in case of any error
     */
    @Event() myTiSearchListErrorEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerErrorEventDetail>;

    /**
     * Event to be emitted in case of list selected successfully
     */
    @Event() myTiSearchListSelectedEvent: EventEmitter<ListManagerInterfaces.MyTiSearchListSelectedEventDetail>;

    componentWillLoad() {
        // load language strings for locale - fallback to 'en-US' or an empty object
        this._localeStrings = languageStrings[this.locale] || languageStrings['en-US'] || {};

        // init data
        this._initData();
    }

    /**
     * Event listener to remove tags when user click 'X' to remove the tags
     */
    @Listen('tiChipChange')
    onTiChipChange(event: CustomEvent<{ group: string; removed?: boolean; selected: boolean; type: string; value: any; }>) {
        if (event.detail.removed) {
            this._tags.delete(event.detail.value);
            this._addTagsBtnRef.disabled = (this._tags.size >= this._tagLimit);
            this._modifiedTags = true;
            this._onSelectedListUpdate(true);
        }
    }

    /**
     * Method to get the selected list with updated tags outside of listening to events
     *
     * @returns the selected list with any updated tags
     */
    @Method()
    async getSelectedList() {
        return this.selectedList;
    }

    /**
     * Method to add tags on user input
     */
    private _addTags() {
        if (this._tags.size >= this._tagLimit || !this.selectedList) {
            this._addTagsBtnRef.disabled = true;
        } else if (this._tagInputElementRef.value && !this._tags.has(this._tagInputElementRef.value)) {
            this._modifiedTags = true;
            this._tags.add(this._tagInputElementRef.value);
            this._tagsDivRef.innerHTML += `<ti-chip appearance="compact" value="${this._tagInputElementRef.value}" removable></ti-chip>`;
            this._tagInputElementRef.value = '';
            this._addTagsBtnRef.disabled = (this._tags.size >= this._tagLimit);
            this._onSelectedListUpdate(true);
        }
    }

    /**
     * Method to format date as (Mon dd, yyyy)
     */
    private _formateDate(dateStr: string) {
        const actualDate = new Date(dateStr);
        return actualDate.toLocaleString('en-us', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    /**
     * Make api call to get the app settings
     */
    private async _getAppSettings(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/settings'
            : 'data/add-to-myti-list/app-settings.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting App Settings');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.AppSettings) => {
                this._appSettings = data;
            });
        } catch (err) {
            this.myTiSearchListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
        }

        if (!this._tagLimit) {
            this._tagLimit = this._appSettings?.maxTagsPerList ?? ListManagerInterfaces.DEFAULT_TAG_LIMIT;
        }
    }

    /**
     * Return error content
     */
    private _getErrorContent() {
        return <div><p>{this._localeStrings['searchListsErrorText']}</p></div>;
    }

    /**
     * Return the loading spinner
     */
    private _getLoadingContent() {
        return <ti-progress></ti-progress>;
    }

    /**
     * Returns the options for the combolist
     */
    private _getListNamesForCombolist(): Item[] {
        return this._userLists
            ?.filter((list: ListManagerInterfaces.ListInfo) => {
                // filter-out if skipList is there
                if (this.skipList && list.listId === this.skipList) {
                    return false;
                }
                // show only users private lists & currentCpId lists
                return !list.sharedCpId || (list.sharedCpId && list.sharedCpId === this._userInfo.currentCpId);
            })
            ?.map((list: ListManagerInterfaces.ListInfo) => {
                return {
                    key: list.listId.toString(),
                    label: list.name
                };
            });
    }

    /**
     * Return the actual search lists component content
     */
    private _getSearchListsContent() {
        return (
            <div>
                <ti-form-element ref={el => this._listFormField = el} label-text={this.headerLabelText} class="ti-search-myti-lists-form-row">
                    <ti-search-combolist
                        placeholder={this._localeStrings['searchListsLabel']}
                        suggestionList={this._getListNamesForCombolist()}
                        onTiSearchComboListSelect={(e) => this._onTiSearchComboListSelect(e)}>
                        <p slot="no-results">
                            {this._userLists?.length > 0
                                ? this._localeStrings['noMatchingListsErrorText']
                                : this._localeStrings['noListsCreatedErrorText']}
                        </p>
                    </ti-search-combolist>
                    {this.listNameActionBtn &&
                        <ti-button
                            appearance='link'
                            onClick={() => this.myTiListNameActionBtnEvent.emit({ contextId: this.contextId })}
                            class="ti-search-myti-lists-action-btn">
                            {this.listNameActionBtnText}
                        </ti-button>
                    }
                    <span slot="error">{this._listFormField?.errorMsg}</span>
                </ti-form-element>

                {this.showTags && this.modifyTags &&
                    <ti-form-element label-text={this._localeStrings['tagsLabel']} class="ti-search-myti-lists-form-row">
                        <ti-input
                            placeholder={this._localeStrings['tagInputPlaceholder']}
                            ref={(el) => this._tagInputElementRef = el}
                            onKeyDown={(e) => {
                                if (e.code === 'Enter') {
                                    this._addTags();
                                }
                            }}></ti-input>
                        <ti-button
                            ref={(el) => this._addTagsBtnRef = el}
                            appearance='compact secondary'
                            class="ti-search-myti-lists-addTags-btn"
                            disabled={this._tags.size >= this._tagLimit || !this.selectedList}
                            onClick={() => this._addTags()}>
                            {this._localeStrings['addTagsBtnText']}
                        </ti-button>
                    </ti-form-element>
                }

                {this.showTags && <div class="ti-search-myti-lists-tags" ref={(el) => this._tagsDivRef = el}></div>}

                {this.selectedList &&
                    <ul data-di-mask class="ti-search-myti-lists-details">
                        <li> {this._localeStrings['listTypeLabel']}: &nbsp;
                            {this.selectedList.sharedCpId ?
                                this._localeStrings['listTypeSharedText'] : this._localeStrings['listTypePrivateText']}</li>
                        <li>  {this._localeStrings['listCreatedByLabel']}: &nbsp;
                            {this.selectedList.ownerName}</li>
                        <li> {this._localeStrings['listDateCreatedLabel']}: &nbsp;
                            {this._formateDate(this.selectedList.createdDate)}</li>
                    </ul>
                }
            </div>
        );
    }

    /**
     * Make api call to get user info
     */
    private async _getUserInfo() {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/user'
            : 'data/add-to-myti-list/user-info.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Info');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.UserInfo) => {
                this._userInfo = data;
            });
        } catch (err) {
            this.myTiSearchListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
            this.progressState = 'error';
        }
    }

    /**
     * Make api call to get all the list infos for the user
     */
    private async _getUserList(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/list'
            : 'data/add-to-myti-list/all-lists.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Lists');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo[]) => {
                this._userLists = data;
            });
        } catch (err) {
            this.myTiSearchListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
            this.progressState = 'error';
        }
    }

    /**
     * To make the required api calls and get the necessary data
     */
    private async _initData() {
        this._initProps();
        // if userInfo prop is null, then make-api call to get the info
        if (!this._userInfo) {
            await this._getUserInfo();
        }

        if (this.progressState !== 'error') {
            let promise1: Promise<void>;
            let promise2: Promise<void>;

            // if userLists prop is null, then make the api-call to get the info
            if (!this._userLists) {
                promise1 = this._getUserList();
            }

            // if listLimit or tagLimit prop is null, then make the api call to get the appSettings
            if ((!this._tagLimit)) {
                promise2 = this._getAppSettings();
            }

            await Promise.all([promise1, promise2]);

            // if there is no error, then the component is ready to be rendered
            if (this.progressState === 'loading') {
                this.progressState = 'ready';
            }
        }
    }

    /**
     * Method to get get data from props, and
     * set it to private variables to avoid data manipulation from client-side
     */
    private _initProps() {
        this._tagLimit = this.tagLimit;
        this._userInfo = this.userInfo;
        this._userLists = this.userLists;
    }

    /**
     * Generic method to make all api calls
     */
    private async _makeApiCall(apiUrl: string,
        methodType: ListManagerInterfaces.HttpMethodType,
        requestBody: any,
        timeout: number = ListManagerInterfaces.LIST_API_TIMEOUT_VALUE
    ): Promise<Response> {
        const apiResponse: Response = await fetchWithTimeout(apiUrl,
            {
                method: methodType,
                body: requestBody !== null && requestBody !== undefined ? JSON.stringify(requestBody) : null,
                credentials: 'include',
                headers: {
                    'Cache-Control': 'no-store, must-revalidate',
                    'Expires': '0',
                    'Content-Type': 'application/json'
                }
            }, timeout);

        return apiResponse;
    }

    /**
     * To update selected list with new set of tags and emit the success event
     */
    private _onSelectedListUpdate(newTags: boolean = false) {
        if (newTags) {
            this.selectedList.tags = [];
            this._tags.forEach(tag => {
                this.selectedList.tags.push({
                    listId: this.selectedList.listId, // validate in service
                    name: tag, // the actual tag
                    tagId: null // populated from service
                });
            });
        }

        // if item limit is violated, show error message, else emit selected event
        if (this.selectedList.totalItemCount < this.selectedList.maxItemLimit) {
            this.myTiSearchListSelectedEvent.emit({
                contextId: this.contextId,
                selectedList: this.selectedList,
                modifiedTags: this._modifiedTags
            });
            this._listFormField.errorMsg = '';
            this._listFormField.error = false;
        } else {
            this._listFormField.errorMsg = this._localeStrings['itemLimitErrorText'].replace('{0}', this.selectedList.maxItemLimit.toString());
            this._listFormField.error = true;
            this.myTiSearchListErrorEvent.emit({ contextId: this.contextId, errorCode: null, errorMsg: 'item limit violated' });
        }
    }

    /**
     * Listener for event when an option from list is selected
     */
    private _onTiSearchComboListSelect(event: CustomEvent<{ value: string, event: Event, eventType: string }>) {
        const selectedListId = Number(event.detail.value);

        // if selected list didn't change, do nothing
        if (this.selectedList?.listId !== selectedListId) {
            this.selectedList = { ...this._userLists.filter(list => list.listId === selectedListId).pop() };

            // if selected list did change and is valid, repopulate tags and emit success event with selected list
            if (this.selectedList) {
                this._tags = new Set(this.selectedList.tags.map(tag => tag.name));

                if (this.showTags) {
                    this._tagsDivRef.innerHTML = '';
                    this._tags.forEach(tag => this._tagsDivRef.innerHTML += `<ti-chip appearance="compact" value="${tag}" removable></ti-chip>`);
                }

                this._onSelectedListUpdate();
            }
        }
    }

    render() {
        switch (this.progressState) {
            case 'ready': return <Host>{this._getSearchListsContent()}</Host>;
            case 'loading': return <Host>{this._getLoadingContent()}</Host>;
            case 'error': return <Host>{this._getErrorContent()}</Host>;
        }
    }

}
